import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { ColumnLayoutService } from '@frontend/sports/layout/feature';
import { WidgetPage } from '@frontend/sports/types/components/widget';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { CrmOfferRestrictionService } from 'packages/sports/web/app/src/crm-offer-data/crm-offer-restriction.service';
import { PickSourceProvider } from 'packages/sports/web/app/src/option-pick/pick-source.provider';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { WidgetComponent } from 'packages/sports/web/app/src/widget/core/widget-registry.service';
import { Observable, map } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { RemindMeLaterService } from '../../../../digital/services/remind-me-later.service';
import { RewardTokensActions } from '../../actions';
import { AvailableTokensType, IRewardToken, IRewardTokenInfo, RewardBannerTrackingConstants } from '../../reward-tokens.model';
import { selectRewardTokensList } from '../../selectors';
import { AcquisitionRewardTokenService } from '../../services/acquisition-reward-token.service';
import { RewardTokensBannerService } from '../../services/reward-tokens-banner.service';
import { getSortedNonAccaTokens, getSortedNonAccaTokensWithExpiryDate, getTokensType } from '../../services/reward-tokens.utils';

@Component({
    selector: 'bs-reward-tokens-banner',
    templateUrl: 'reward-tokens-banner.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./reward-tokens-banner.component.scss'],
    standalone: false,
})
export class RewardTokensBannerComponent extends WidgetComponent<never> implements OnInit {
    viewData = <ITokensBannerViewData>{ icon: '', title: '' };
    tokenInfos: IRewardTokenInfo[];
    isRewardDrawerEnabled = false;
    isTokenSortingEnabled = false;
    scrollHeight = 0;
    isDarkMode = false;
    isRemindMeLaterEnabled: boolean;
    showRemindMeLater = false;

    private rewardTokensState$: Observable<IRewardToken[]>;
    private _promoRestrictedMessage: string;
    private isBannerTrackedOnLoad = false;
    readonly messagesAndConfigs: {
        dismissLinkText: string;
        showDismissLink: boolean;
    };

    @HostBinding('class') className = 'reward-tokens-banner reward-tokens-module cards-module';
    @HostBinding('class.reward-tokens-banner--has-tokens') hasTokens = false;
    @HostBinding('class.reward-tokens-banner--restricted') isPromoRestricted = false;
    @HostBinding('class.reward-tokens-banner--dismissed') isBannerDismissed = false;
    @HostBinding('class.reward-tokens-banner--expanded') isBannerExpanded = false;

    @HostBinding('class.reward-tokens-banner--expandable') get isExpandableBannerEnabled() {
        return this.tokensConfig.tokens.isExpandableRewardBannerEnabled;
    }

    @HostBinding('class.reward-tokens-banner--dark') get isDarkMoreEnabled() {
        return this.isDarkMode;
    }

    @HostBinding('class.reward-tokens-banner-enabled') get isRewardDrawerBannerEnabled() {
        return this.tokensConfig.tokens.isRewardDrawerEnabled;
    }

    @ViewChild('tokensBannerContainer') container: ElementRef;

    constructor(
        public layoutManager: ColumnLayoutService,
        public sitecore: Sitecore,
        private tokensConfig: CrmConfig,
        private changeDetector: ChangeDetectorRef,
        private store: Store<IBetslipRootState>,
        private pickSourceProvider: PickSourceProvider,
        private crmOfferRestrictionService: CrmOfferRestrictionService,
        private rewardTokensBannerService: RewardTokensBannerService,
        private configAccessor: ModularConfigAccessorService,
        private deviceService: DeviceService,
        private remindMeLaterService: RemindMeLaterService,
        private acquisitionRewardTokenService: AcquisitionRewardTokenService,
    ) {
        super();
        this.rewardTokensState$ = this.store.select(selectRewardTokensList);
        this._promoRestrictedMessage = interpolateDoubleBracesString(this.sitecore.crmTokens.banner.BannerPromoRestricted, {
            link: this.promotionsUrl,
        });
        this.messagesAndConfigs = {
            dismissLinkText: this.sitecore.crmTokens.banner.RewardBannerDismissOrRemindMeLaterTitle,
            showDismissLink: this.tokensConfig.tokens.isDismissOrRemindMeLaterEnabled,
        };
        this.isDarkMode = this.tokensConfig.tokens.enableDarkModeForRewardDrawer;
        this.isRemindMeLaterEnabled = this.tokensConfig.tokens.remindMeLaterNotificationEnabled;
    }

    ngOnInit(): void {
        this.remindMeLaterService.remindMeNotification$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((showTokens) => {
            this.isBannerDismissed = showTokens || this.rewardTokensBannerService.isBetRewardBannerDismissed();
            this.changeDetector.detectChanges();
        });

        this.isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;
        this.isTokenSortingEnabled = this.tokensConfig.tokens.isTokenSortingEnabled;
        this.rewardTokensBannerService.setDefaultValueForBannerExpanded();
        this.rewardTokensBannerService.isBannerExpanded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((expanded) => {
            this.isBannerExpanded = expanded;
            this.addBannerTracking();
        });
        this.rewardTokensState$
            .pipe(
                map((data) =>
                    this.isTokenSortingEnabled
                        ? getSortedNonAccaTokensWithExpiryDate(data, this.tokensConfig.tokens.isVIPBadgeEnabled)
                        : getSortedNonAccaTokens(data),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((tokens) => {
                this.isBannerDismissed = this.rewardTokensBannerService.isBetRewardBannerDismissed();
                this.tokenInfos = tokens.map((token) => ({ token, selected: false }));
                this.hasTokens = tokens.length > 0;
                if (this.hasTokens) {
                    this.isBannerExpanded = this.acquisitionRewardTokenService.shouldShowAcquisitionRewardNotification(tokens);
                    this.viewData = this.getBannerViewData(tokens);

                    if (!this.isBannerTrackedOnLoad && this.isExpandableBannerEnabled && !this.isBannerDismissed && this.hasTokens) {
                        this.trackBanner(RewardBannerTrackingConstants.Load);
                        this.isBannerTrackedOnLoad = true;
                    }
                    this.hidden = false; //setting to false once we have tokens as the default value will be true due to ordering logic.
                }

                this.changeDetector.markForCheck();
            });

        this.crmOfferRestrictionService.promoRestricted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isRestricted) => {
            this.isPromoRestricted = isRestricted;
            this.changeDetector.markForCheck();
        });
    }

    get promotionsUrl(): string {
        return this.sitecore.crmTokens.links.promotions.url || '';
    }

    get promoRestrictedMessage(): string {
        return this._promoRestrictedMessage;
    }

    get isShowRemindMeLaterNotification(): boolean {
        return !this.isRewardDrawerEnabled && this.showRemindMeLater && this.isRemindMeLaterEnabled;
    }

    @HostListener('click')
    clicked(): void {
        if (this.isExpandableBannerEnabled || this.isPromoRestricted) {
            return;
        }

        this.store.dispatch(
            RewardTokensActions.bannerClick({
                banner: this.promotionsUrl,
                trackingSource: this.pickSourceProvider.getTracking(),
            }),
        );
    }

    toggleBannerExpansion(): void {
        this.rewardTokensBannerService.toggleBannerExpansion();
    }

    hideBanner(event: Event): void {
        if (this.isRewardDrawerEnabled) {
            event.stopPropagation();
            this.trackBanner(RewardBannerTrackingConstants.Click, 'Remind me later');
            this.rewardBannerClosed();
        } else if (this.isRemindMeLaterEnabled) {
            if (this.deviceService.isMobile && !this.deviceService.isTablet) {
                this.isBannerDismissed = true;
                this.remindMeLaterService.showTokensReminder(true);
            } else {
                this.showRemindMeLater = true;
            }
        } else {
            this.rewardBannerClosed();
        }
    }

    undoReminder(): void {
        this.showRemindMeLater = false;
    }

    closeReminder(): void {
        this.isBannerDismissed = true;
    }

    private addBannerTracking(): void {
        this.trackBanner(this.isBannerExpanded ? RewardBannerTrackingConstants.Expand : RewardBannerTrackingConstants.Collapse);
    }

    private rewardBannerClosed(): void {
        this.isBannerDismissed = true;
        this.rewardTokensBannerService.setBetRewardBannerStorageKey(true);
    }

    private getBannerViewData(tokens: IRewardToken[]): ITokensBannerViewData {
        const messages = this.sitecore.crmTokens.banner;

        if (!messages) {
            return { icon: '', title: '' };
        }

        if (this.isRewardDrawerEnabled) {
            return {
                icon: '',
                title: interpolateDoubleBracesString(messages.BannerTitleMultipleRewards, { count: tokens.length.toString() }),
            };
        }

        const availableTokensType = getTokensType(tokens) as Exclude<AvailableTokensType, AvailableTokensType.AccaBoost>;

        switch (availableTokensType) {
            case AvailableTokensType.FreeBet:
                return {
                    title: this.getTitle(tokens, messages.BannerTitleSingleFreeBet, messages.BannerTitleFreebets),
                    icon: this.tokensConfig.tokens.icons.freeBet ?? '',
                };
            case AvailableTokensType.OddsBoost:
                return {
                    title: this.getTitle(tokens, messages.BannerTitleSingleOddsBoost, messages.BannerTitleOddsBoosts),
                    icon: this.tokensConfig.tokens.icons.oddsBoost ?? '',
                };
            case AvailableTokensType.RiskFreeBet:
                return {
                    title: this.getTitle(tokens, messages.BannerTitleSingleRiskFree, messages.BannerTitleRiskFreeBets),
                    icon: this.tokensConfig.tokens.icons.riskFree,
                };
            case AvailableTokensType.BetAndGet:
                return {
                    title: this.getTitle(tokens, messages.BannerTitleSingleBetAndGet, messages.BannerTitleBetAndGet),
                    icon: this.tokensConfig.tokens.icons.betAndGet ?? '',
                };
            case AvailableTokensType.MultipleRewards:
                return {
                    title: interpolateDoubleBracesString(messages.BannerTitleMultipleRewards, { count: tokens.length.toString() }),
                    icon: '',
                };
        }
    }

    private getTitle(tokens: IRewardToken[], singleTokenTitle: string, multipleTokensTemplate: string): string {
        const count = tokens.length;

        if (count === 1) {
            if (tokens[0].rewardTokenType === RewardTokenType.BetAndGet) {
                return singleTokenTitle;
            }
            const customTitle = tokens[0].customTitle;

            return customTitle ? interpolateDoubleBracesString(this.sitecore.crmTokens.banner.BannerCustomTitle, { customTitle }) : singleTokenTitle;
        } else {
            return interpolateDoubleBracesString(multipleTokensTemplate, { count: count.toString() });
        }
    }

    private trackBanner(action: RewardBannerTrackingConstants, eventDetails?: string): void {
        eventDetails = eventDetails ? eventDetails : RewardBannerTrackingConstants.BetRewardsBanner;
        const page = this.configAccessor.getPage();
        this.rewardTokensBannerService.trackBetRewardBanner(
            action,
            this.pickSourceProvider.getTracking(),
            eventDetails,
            undefined,
            undefined,
            page === WidgetPage.EventDetailsPage ? 'event details page' : undefined,
        );
    }
}

interface ITokensBannerViewData {
    title: string;
    icon?: string;
}
